import type { FilterExpression, FilterField, FilterGroup, FilterRule, FilterSchema, FilterState } from './types'

export const operators: Record<string, string> = { in: 'Равно', not_in: 'Не равно', eq: 'Равно', contains: 'Содержит', not_contains: 'Не содержит', gte: 'Не раньше / не меньше', lte: 'Не позже / не больше', empty: 'Не заполнено', filled: 'Заполнено' }
export const emptyState = (): FilterState => ({ search: '', filter: { match: 'all', conditions: [] } })
export const copyState = (state: FilterState): FilterState => JSON.parse(JSON.stringify(state))
export const isGroup = (rule: FilterRule | FilterGroup): rule is FilterGroup => 'scope' in rule
export function newRule(field: FilterField): FilterRule {
  const operator = field.operators[0]
  return { field: field.key, operator, value: ['in', 'not_in'].includes(operator) ? [] : '' }
}
export function fieldError(rule: FilterRule, fields: FilterField[]): string {
  const field = fields.find(f => f.key === rule.field)
  if (!field || !field.operators.includes(rule.operator)) return 'Поле недоступно. Удалите его или выберите другое.'
  if (['empty', 'filled'].includes(rule.operator)) return ''
  const values = Array.isArray(rule.value) ? rule.value : [rule.value]
  if (!values.length || values.some(v => !v?.trim())) return 'Укажите значение'
  if (values.some(v => String(v).length > 500) || values.length > 50) return 'Сократите значение условия'
  if (field.options && values.some(v => !field.options!.some(o => o.value === v))) return 'Выберите доступное значение'
  if (field.type === 'number' && values.some(v => !/^\d+$/.test(v!) || Number(v) <= 0)) return 'Укажите положительный номер'
  return ''
}
export function validateState(state: FilterState, schema: FilterSchema): string {
  if (state.search.length > 500) return 'Поиск: не более 500 символов'
  let count = state.filter.conditions.length
  for (const item of state.filter.conditions) {
    if (isGroup(item)) {
      const scope = schema.scopes.find(s => s.key === item.scope)
      if (!scope) return 'Анкета недоступна. Обновите фильтр.'
      count += item.conditions.length
      if (item.conditions.some(rule => fieldError(rule, scope.fields))) return 'Заполните выделенные условия'
    } else if (fieldError(item, schema.fields)) return 'Заполните выделенные условия'
  }
  return count > 30 ? 'Допустимо не более 30 условий' : ''
}
export function summarize(item: FilterRule | FilterGroup, schema: FilterSchema, labels: Record<string, string> = {}): string {
  if (isGroup(item)) {
    const scope = schema.scopes.find(s => s.key === item.scope)
    const details = item.conditions.map(r => summarize(r, { fields: scope?.fields || [], scopes: [] }, labels))
    return `${scope?.label || 'Недоступная анкета'}${details.length ? `: ${details.join(item.match === 'all' ? '; ' : ' или ')}` : ''}`
  }
  const field = schema.fields.find(f => f.key === item.field)
  const values = Array.isArray(item.value) ? item.value : item.value ? [item.value] : []
  const formatted = values.map(v => field?.options?.find(o => o.value === v)?.label || labels[`${item.field}:${v}`] || v).join(', ')
  const operator = item.operator === 'gte' && field?.type === 'date' ? 'с' : item.operator === 'lte' && field?.type === 'date' ? 'по' : operators[item.operator]?.toLowerCase()
  return `${field?.label || item.field}: ${operator} ${formatted}`.trim()
}
export function expressionCount(expression: FilterExpression): number {
  return expression.conditions.reduce((n, item) => n + (isGroup(item) ? Math.max(1, item.conditions.length) : 1), 0)
}

/** Reject malformed URL/preset structures before Vue renders their nodes. */
export function isExpression(value: unknown): value is FilterExpression {
  if (!value || typeof value !== 'object') return false
  const node = value as Record<string, unknown>
  if (!['all', 'any'].includes(String(node.match)) || !Array.isArray(node.conditions) || node.conditions.length > 30) return false
  const leaf = (r: any) => r && typeof r === 'object' && typeof r.field === 'string' && typeof r.operator === 'string'
    && (r.value === undefined || typeof r.value === 'string' || (Array.isArray(r.value) && r.value.every((v: unknown) => typeof v === 'string')))
  return node.conditions.every(r => leaf(r) || (r && typeof r.scope === 'string' && ['all', 'any'].includes(r.match)
    && Array.isArray(r.conditions) && r.conditions.length <= 30 && r.conditions.every(leaf)))
}

export function statesEqual(left: FilterState, right: FilterState): boolean {
  const canonical = (value: any): any => Array.isArray(value)
    ? value.map(canonical)
    : value && typeof value === 'object' ? Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])])) : value
  return JSON.stringify(canonical(left)) === JSON.stringify(canonical(right))
}
